import React, { useState, useEffect } from 'react';
import { Lightbulb, PenLine } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface DailyPromptCardProps {
  onUsePrompt?: (text: string) => void;
}

export default function DailyPromptCard({ onUsePrompt }: DailyPromptCardProps) {
  const [prompt, setPrompt] = useState<{ id: string; prompt_text: string; category: string; response_count: number } | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(`daily-prompt-dismissed:${today}`)) {
      setDismissed(true);
      return;
    }
    fetch(`${API_URL}/engagement/daily-prompt`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.prompt) setPrompt(d.prompt); })
      .catch(() => {});
  }, []);

  if (!prompt || dismissed) return null;

  const dismiss = () => {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem(`daily-prompt-dismissed:${today}`, '1');
    setDismissed(true);
  };

  return (
    <div className="mb-4 rounded-xl bg-linear-to-r/srgb from-iris-500/10 to-iris-500/10 border border-iris-500/20 p-4">
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-iris-500/20 shrink-0">
          <Lightbulb size={16} className="text-iris-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-iris-400 mb-1">Daily Prompt</p>
          <p className="text-sm text-white leading-relaxed">{prompt.prompt_text}</p>
          {prompt.response_count > 0 && (
            <p className="text-[11px] text-[#9a9aac] mt-1">{prompt.response_count} responses</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 ml-11">
        <button
          onClick={() => onUsePrompt?.(prompt.prompt_text)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-iris-500/20 text-xs font-medium text-iris-300 hover:bg-iris-500/30 transition-colors"
        >
          <PenLine size={12} />
          Write about this
        </button>
        <button
          onClick={dismiss}
          className="text-[11px] text-[#9a9aac] hover:text-white transition-colors px-2 py-1"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
